import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable, Subject, catchError, forkJoin, map, of, switchMap, takeUntil } from 'rxjs';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { CompraService } from '@shared/services/apis/compras/compra.service';
import { NotaFiscalEntradaService } from '@shared/services/apis/orquestrador-compras/nota-fiscal-entrada.service';
import { DetalheCompraResponse } from '@shared/services/responses/compras/detalhe-compra.response';
import { NotaFiscalEntradaContextoVinculoPedidoResponse } from '@shared/services/responses/orquestrador-compras/nota-fiscal-entrada-contexto-vinculo-pedido.response';
import { CompraItemStruct } from '@shared/services/structs/compras/compra-item.struct';
import { VinculoPedidoNotaItemStruct } from '@shared/services/structs/orquestrador-compras/vinculo-pedido-nota-item.struct';
import { VinculoPedidoNotaStruct } from '@shared/services/structs/orquestrador-compras/vinculo-pedido-nota.struct';
import { CardNotaPedido, fmtMoneyDisplay, formatCodigoCompra } from '../card-edit-dialog.types';

const ERRO_CARGA = 'Não foi possível carregar os itens da nota.';

/** Nota carregada: o card monta com isso a seção "Nota fiscal" e a lista de pedidos da coluna esquerda. */
export interface NotaItensCarregados {
  nota: VinculoPedidoNotaStruct;
  nomeEmitente: string;
  /** Pedidos vinculados à nota: os do quadro primeiro, na ordem dele. */
  pedidos: CardNotaPedido[];
}

/** Linha da tabela: um item da NF no grupo do pedido a que foi vinculado (ou sem vínculo). */
interface LinhaItemVm {
  chave: string;
  numeroItem: number;
  descricao: string;
  codigoEmitente: string | null;
  quantidade: number | null;
  unidade: string | null;
  valorUnitario: number | null;
  /** "#24 · Nome do item do pedido"; nulo = item da NF sem vínculo. */
  itemPedido: string | null;
  quantidadeVinculada: number | null;
  unidadePedido: string | null;
}

interface GrupoItensVm {
  chave: string;
  idCompra: number | null;
  titulo: string;
  nomeEtapa: string | null;
  linhas: LinhaItemVm[];
}

/**
 * "Itens da nota" do card no modo NF: itens da nota inteira agrupados pelo pedido vinculado (o pedido
 * do card primeiro, destacado) e os itens sem vínculo. Só leitura; o vínculo segue no ced-stage-nota.
 */
@Component({
  selector: 'app-ced-nota-itens',
  standalone: true,
  imports: [MatIconModule, MatProgressSpinnerModule, AlertCardComponent, AbntPipe],
  templateUrl: './ced-nota-itens.component.html',
  styleUrl: './ced-nota-itens.component.css',
})
export class CedNotaItensComponent implements OnDestroy {
  @Input({ required: true }) idNFeEntrada!: number;
  /** Pedidos da moldura do quadro: ordem, código, fornecedor e valor que o card já resolveu. */
  @Input() pedidos: CardNotaPedido[] = [];
  @Input() idCompraAtual: number | null = null;
  /** Detalhe do pedido aberto, que o card já tem: não é buscado de novo. Vem antes de `contexto` no template. */
  @Input() detalheAtual: DetalheCompraResponse | null = null;
  /** Conferência cega não concluída: oculta a coluna Qtd. e a quantidade vinculada. */
  @Input() ocultarQuantidades = false;
  /**
   * Contexto da nota já carregado pelo bloco da NF da etapa (ced-stage-nota), a cada carga dele — inclusive
   * depois de um vínculo alterado. `undefined` = aguardando o bloco; `null` = etapa sem o bloco ou ele falhou:
   * a tabela carrega sozinha.
   */
  @Input() set contexto(valor: NotaFiscalEntradaContextoVinculoPedidoResponse | null | undefined) {
    if (valor === undefined) return;
    this.carregar(valor ? of(valor) : this.notaFiscalEntradaService.contextoVinculoPedido(this.idNFeEntrada));
  }
  /** Emite também na falha ou sem a nota (`null`), para o card encerrar o carregamento da seção "Nota fiscal". */
  @Output() carregado = new EventEmitter<NotaItensCarregados | null>();

  carregando = true;
  erro: string | null = null;
  grupos: GrupoItensVm[] = [];
  qtdItensNota = 0;
  qtdItensSemVinculo = 0;

  private readonly destroy$ = new Subject<void>();
  private readonly recarga$ = new Subject<void>();

  constructor(
    private notaFiscalEntradaService: NotaFiscalEntradaService,
    private compraService: CompraService,
    private alertService: AlertService,
  ) {}

  ngOnDestroy(): void {
    this.recarga$.next();
    this.recarga$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Contexto de vínculo da nota (cabeçalho, itens e vínculos item a item) e, em seguida, o detalhe de
   * cada pedido vinculado, só para o nome dos itens do pedido; o do pedido aberto vem do card.
   * Detalhe que falhar não impede a tabela.
   */
  private carregar(contexto$: Observable<NotaFiscalEntradaContextoVinculoPedidoResponse>): void {
    this.recarga$.next();
    this.carregando = true;
    this.erro = null;
    contexto$.pipe(
      switchMap(contexto => {
        const idsCompra = contexto.isError ? [] : [...new Set((contexto.vinculosPedido ?? []).map(v => v.idCompra))];
        const detalhes$: Observable<(DetalheCompraResponse | null)[]> = idsCompra.length
          ? forkJoin(idsCompra.map(id => id === this.idCompraAtual && this.detalheAtual
            ? of(this.detalheAtual)
            : this.compraService.getDetalhe(id).pipe(catchError(() => of(null)))))
          : of([]);
        return detalhes$.pipe(map(detalhes => ({ contexto, detalhes })));
      }),
      takeUntil(this.recarga$),
      takeUntil(this.destroy$),
    ).subscribe({
      next: ({ contexto, detalhes }) => {
        this.carregando = false;
        if (contexto.isError) {
          this.erro = contexto.errorDescription || ERRO_CARGA;
          this.alertService.show('Erro', this.erro, AlertType.error);
          this.carregado.emit(null);
          return;
        }
        this.montar(contexto, detalhes);
      },
      error: () => {
        this.carregando = false;
        this.erro = ERRO_CARGA;
        this.alertService.show('Erro', ERRO_CARGA, AlertType.error);
        this.carregado.emit(null);
      },
    });
  }

  private montar(contexto: NotaFiscalEntradaContextoVinculoPedidoResponse, detalhes: (DetalheCompraResponse | null)[]): void {
    const itensCompra = new Map<number, CompraItemStruct>();
    const valorPorCompra = new Map<number, number | null>();
    detalhes.forEach(detalhe => {
      if (!detalhe || detalhe.isError) return;
      (detalhe.itens ?? []).forEach(item => itensCompra.set(item.idCompraItem, item));
      if (detalhe.compra) valorPorCompra.set(detalhe.compra.idCompra, detalhe.compra.valorTotal ?? null);
    });

    // A RepoDFe pode não trazer o nome do emitente: vale o fornecedor dos pedidos do quadro.
    const nomeEmitente = contexto.nota?.nomeEmitente
      || this.pedidos.find(p => !!p.nomeFornecedor)?.nomeFornecedor
      || contexto.nota?.cnpjEmitente
      || 'Emitente não informado';

    const itensNota = [...(contexto.itens ?? [])].sort((a, b) => a.numeroItem - b.numeroItem);
    const itemNotaPorId = new Map(itensNota.map(item => [item.idNFeEntradaItem, item]));
    const linhaDaNota = (item: VinculoPedidoNotaItemStruct, chave: string): LinhaItemVm => ({
      chave,
      numeroItem: item.numeroItem,
      descricao: item.descricao,
      codigoEmitente: item.codigoProdutoEmitente,
      quantidade: item.quantidadeComercial,
      unidade: item.unidadeComercial,
      valorUnitario: item.valorUnitarioComercial,
      itemPedido: null,
      quantidadeVinculada: null,
      unidadePedido: null,
    });

    // Pedidos da moldura primeiro, na ordem do quadro; os demais vínculos da nota depois, pelo código.
    const ordemQuadro = new Map(this.pedidos.map((p, i) => [p.idCompra, i]));
    const doQuadro = new Map(this.pedidos.map(p => [p.idCompra, p]));
    const vinculos = [...(contexto.vinculosPedido ?? [])].sort((a, b) =>
      (ordemQuadro.get(a.idCompra) ?? Number.MAX_SAFE_INTEGER) - (ordemQuadro.get(b.idCompra) ?? Number.MAX_SAFE_INTEGER)
      || (a.codigoCompra ?? '').localeCompare(b.codigoCompra ?? '', undefined, { numeric: true }));

    const pedidos: CardNotaPedido[] = vinculos.map(v => {
      const quadro = doQuadro.get(v.idCompra);
      const valor = fmtMoneyDisplay(valorPorCompra.get(v.idCompra));
      return {
        idCompra: v.idCompra,
        codigo: quadro?.codigo || formatCodigoCompra(v.codigoCompra),
        nomeFornecedor: quadro?.nomeFornecedor || nomeEmitente,
        nomeEtapa: v.nomeEtapa || quadro?.nomeEtapa || '—',
        corEtapa: v.corEtapa,
        iconeEtapa: v.iconeEtapa,
        valorLabel: quadro?.valorLabel || (valor === '—' ? '—' : `R$ ${valor}`),
        qtdItensNota: new Set((v.itens ?? []).map(l => l.idNFeEntradaItem)).size,
      };
    });

    const vinculados = new Set<number>();
    const grupos: GrupoItensVm[] = vinculos.map((v, i) => {
      const pedido = pedidos[i];
      const linhas = (v.itens ?? [])
        .filter(l => itemNotaPorId.has(l.idNFeEntradaItem))
        .map(l => {
          vinculados.add(l.idNFeEntradaItem);
          const itemCompra = itensCompra.get(l.idCompraItem);
          return {
            ...linhaDaNota(itemNotaPorId.get(l.idNFeEntradaItem)!, `v${l.idCompraItemNFeEntradaItemVinculo}`),
            itemPedido: `${pedido.codigo} · ${itemCompra?.nome || 'Item do pedido'}`,
            quantidadeVinculada: l.quantidadeVinculada,
            unidadePedido: itemCompra?.unidade || null,
          };
        })
        .sort((a, b) => a.numeroItem - b.numeroItem);
      return { chave: `c${v.idCompra}`, idCompra: v.idCompra, titulo: `Pedido ${pedido.codigo}`, nomeEtapa: pedido.nomeEtapa, linhas };
    });
    // O pedido do card abre a tabela.
    const iAtual = grupos.findIndex(g => g.idCompra === this.idCompraAtual);
    if (iAtual > 0) grupos.unshift(...grupos.splice(iAtual, 1));

    const semVinculo = itensNota.filter(item => !vinculados.has(item.idNFeEntradaItem));
    if (semVinculo.length) {
      grupos.push({
        chave: 'sem-vinculo', idCompra: null, titulo: 'Itens sem vínculo', nomeEtapa: null,
        linhas: semVinculo.map(item => linhaDaNota(item, `n${item.idNFeEntradaItem}`)),
      });
    }
    this.grupos = grupos;
    this.qtdItensNota = itensNota.length;
    this.qtdItensSemVinculo = semVinculo.length;
    this.carregado.emit(contexto.nota ? { nota: contexto.nota, nomeEmitente, pedidos } : null);
  }
}
